import { useState } from "react";
import { useGetRecentChargesQuery } from "../../../../api/queries";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import Table, { ColumnsType } from "antd/es/table";
import { DatePicker, Button, Space } from "antd";
import styles from "./RecentCharges.module.scss";
import { TRecentCharge } from "../../../../models/RecentCharge";
import { ClubpassEventType } from "../../../../models/ClassEvent";
import CalendarIcon from "../../../../assets/images/calendar.svg";
import SearchIcon from "../../../../assets/images/search-normal.svg";

export type RecentChargesFormValues = {
  fromDate: string;
  toDate: string;
};

const DATE_FORMAT = "YYYY-MM-DD";
const today = dayjs().format(DATE_FORMAT);
const weekAgo = dayjs().subtract(7, "day").format(DATE_FORMAT);

const datePickerStyle = {
  height: 48,
  color: "rgba(54, 60, 79, 0.50)",
  fontSize: 16,
  fontWeight: 400,
  textAlign: "left" as const,
};

const RecentCharges = () => {
  const { t } = useTranslation();

  const [formRange, setFormRange] = useState<RecentChargesFormValues>({
    fromDate: weekAgo,
    toDate: today,
  });

  const [appliedRange, setAppliedRange] = useState(formRange);

  const {
    data = [],
    isFetching,
    refetch,
    isLoading,
  } = useGetRecentChargesQuery({
    fromDate: appliedRange.fromDate,
    toDate: appliedRange.toDate,
  });

  const handleSearch = () => {
    setAppliedRange(formRange);
    refetch();
  };

  const handleFromDateChange = (date: dayjs.Dayjs | null) => {
    if (!date) return;
    const formatted = date.format(DATE_FORMAT);

    if (dayjs(formatted).isAfter(dayjs(formRange.toDate))) {
      setFormRange((prev) => ({ ...prev, fromDate: prev.toDate }));
    } else {
      setFormRange((prev) => ({ ...prev, fromDate: formatted }));
    }
  };

  const handleToDateChange = (date: dayjs.Dayjs | null) => {
    if (!date) return;
    const formatted = date.format(DATE_FORMAT);

    if (dayjs(formatted).isBefore(dayjs(formRange.fromDate))) {
      setFormRange((prev) => ({ ...prev, toDate: prev.fromDate }));
    } else {
      setFormRange((prev) => ({ ...prev, toDate: formatted }));
    }
  };

  const columns: ColumnsType<TRecentCharge> = [
    {
      title: t("Type"),
      render: (data: TRecentCharge) =>
        data.type === ClubpassEventType.OPEN_GYM ? t("OpenGym") : t("Booking"),
    },
    {
      title: t("Date & Time"),
      render: (data: TRecentCharge) => dayjs(data.createdAt).format("MMM D, YYYY - h:mm A"),
    },
    {
      title: t("Class"),
      render: (data: TRecentCharge) => data.roomName,
    },
    {
      title: t("Amount"),
      render: (data: TRecentCharge) => `${data.creditsValue} ${t("Credits")}`,
    },
    {
      title: t("Refund"),
      render: (data: TRecentCharge) => (
        <p style={{ color: data.refundType ? "#32BC69" : "#EF5D60" }}>
          {data.refundType ? t("Refunded") : t("NotRefunded")}
        </p>
      ),
    },
    {
      title: t("Status"),
      render: (data: TRecentCharge) => (
        <p
          style={{
            color: data.attended ? "#32BC69" : "#FF8C42",
            padding: "5px 10px",
            backgroundColor: data.attended ? "#BDFFD7" : "#FEF3EC",
            textAlign: "center",
            borderRadius: 6,
            display: "inline-block",
            minWidth: 110,
          }}
        >
          {data.attended ? t("Attended") : t("NotAttended")}
        </p>
      ),
    },
  ];

  return (
    <div>
      <h2 className={styles.title}>{t("RecentCharges")}</h2>

      <Space style={{ marginBottom: 24, gap: 16 }}>
        <div>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 8 }}>{t("From")}</label>
          <DatePicker
            value={dayjs(formRange.fromDate)}
            format={DATE_FORMAT}
            style={datePickerStyle}
            onChange={handleFromDateChange}
            suffixIcon={<img src={CalendarIcon} alt="Calendar" />}
            disabledDate={(current) => current && current.isAfter(dayjs(formRange.toDate))}
          />
        </div>

        <div>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 8 }}>{t("To")}</label>
          <DatePicker
            value={dayjs(formRange.toDate)}
            format={DATE_FORMAT}
            style={datePickerStyle}
            onChange={handleToDateChange}
            suffixIcon={<img src={CalendarIcon} alt="Calendar" />}
            disabledDate={(current) => current && current.isBefore(dayjs(formRange.fromDate))}
          />
        </div>

        <Button
          type="primary"
          icon={<img src={SearchIcon} alt="Search" />}
          onClick={handleSearch}
          loading={isFetching}
          style={{
            height: 48,
            width: 81,
            marginTop: 22,
            backgroundColor: "#FF7A00",
            border: "none",
          }}
        />
      </Space>

      <Table
        className="antd-table"
        columns={columns}
        rowKey={(record) => record.id}
        // @ts-ignore
        dataSource={data}
        loading={isLoading}
      />
    </div>
  );
};

export default RecentCharges;
